import { useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import BookingLayout from 'pages/booking/BookingLayout';
import {
  BOOKING_ACCOUNT_BOOKINGS_PATH,
  BOOKING_ACCOUNT_CREDITS_PATH,
  BOOKING_ACCOUNT_CREDIT_PURCHASES_PATH,
  BOOKING_ACCOUNT_INVOICES_PATH,
  BOOKING_ACCOUNT_PROFILE_PATH,
  BOOKING_ACCOUNT_PASSWORD_PATH,
  BOOKING_LOGIN_PATH,
} from 'routes/paths';
import { useApi } from 'api/api-context';
import useLang from 'lang';
import 'pages/booking/account/booking-account.css';

const accountTabPaths = [
  BOOKING_ACCOUNT_BOOKINGS_PATH.original,
  BOOKING_ACCOUNT_INVOICES_PATH.original,
  BOOKING_ACCOUNT_CREDITS_PATH.original,
  BOOKING_ACCOUNT_CREDIT_PURCHASES_PATH.original,
  BOOKING_ACCOUNT_PROFILE_PATH.original,
  BOOKING_ACCOUNT_PASSWORD_PATH.original,
];

/**
 * Shared shell for authenticated booking self-service pages.
 */
export default function BookingAccountLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAction, destroySession } = useApi();
  const { _t } = useLang();
  const [loggingOut, setLoggingOut] = useState(false);
  const tabRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const accountTabs = [
    { path: BOOKING_ACCOUNT_BOOKINGS_PATH.original, translation: _t('Bookings') },
    { path: BOOKING_ACCOUNT_INVOICES_PATH.original, translation: _t('Invoices') },
    { path: BOOKING_ACCOUNT_CREDITS_PATH.original, translation: _t('Credits') },
    { path: BOOKING_ACCOUNT_CREDIT_PURCHASES_PATH.original, translation: _t('Credit purchases') },
    { path: BOOKING_ACCOUNT_PROFILE_PATH.original, translation: _t('Personal details') },
    { path: BOOKING_ACCOUNT_PASSWORD_PATH.original, translation: _t('Password') },
  ];

  useLayoutEffect(() => {
    const activePath = accountTabPaths.find(
      (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
    if (!activePath) return;

    tabRefs.current[activePath]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [location.pathname]);

  /**
   * Invalidates the server session when possible and always clears local authentication.
   */
  const logout = async () => {
    setLoggingOut(true);
    try {
      await getAction('Logout')({});
    } catch {
      // Local logout must still succeed when the remote session is already unavailable.
    }
    destroySession();
    navigate(BOOKING_LOGIN_PATH.original, { replace: true });
  };

  return (
    <BookingLayout>
      <div className="account-page">
        <header className="account-heading">
          <h1>{_t('My account')}</h1>
          <p>{_t('Manage your reservations, invoices and credits.')}</p>
        </header>
        <nav className="account-tabs" aria-label={_t('My account sections')}>
          {accountTabs.map(({ path, translation }) => (
            <NavLink
              key={path}
              ref={(element) => {
                tabRefs.current[path] = element;
              }}
              to={path}
            >
              {translation}
            </NavLink>
          ))}
        </nav>
        {children}
        <footer className="account-logout">
          <button
            className="account-button account-button--secondary"
            disabled={loggingOut}
            onClick={() => void logout()}
          >
            {_t('Log out')}
          </button>
        </footer>
      </div>
    </BookingLayout>
  );
}
